import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { CompanyResponse, CompanyId } from '@business/entities/companies';
import { handleMessage, requestMessage } from '@business/messages';
import { CompanyAPI } from '@business/api/company';
import { Session } from '@modules/auth/session';
import { R } from '@core/utils/r';

export class CompanyBloc extends BaseBloc {
  private readonly companiesRepo = new Repository<CompanyResponse>({
    getItemId: company => company.id,
  });

  readonly companies$ = this.companiesRepo.items$;
  readonly companies = this.companiesRepo.items;
  readonly selectCompany = this.companiesRepo.selectItem;

  onReset() {
    this.companiesRepo.reset();
  }

  fetchCompanies = () => {
    return CompanyAPI.fetchCompanies().pipe(this.companiesRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_companies_error') }));
  };

  fetchCompany = (id: CompanyId) => {
    return CompanyAPI.fetchCompany(id).pipe(
      this.companiesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_companies_error') }),
    );
  };

  createCompany = (company: Partial<CompanyResponse>) => {
    return CompanyAPI.createCompany(company).pipe(
      this.companiesRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_company'),
      }),
    );
  };

  updateCompany = (id: CompanyId, company: Partial<CompanyResponse>) => {
    return CompanyAPI.updateCompany(id, company).pipe(
      this.companiesRepo.ops.upsertOne(item => ({ item })),
      tap(() => Session.reloadUser().subscribe()),
      handleMessage({
        type: requestMessage('update_company'),
      }),
    );
  };

  deleteCompany = (companyId: CompanyId) => {
    return showDeleteConfirmation('Delete Company', 'Do you really want to remove this company?').pipe(
      switchMap(() => CompanyAPI.deleteCompany(companyId)),

      this.companiesRepo.ops.removeOne(() => companyId),
      handleMessage({
        type: requestMessage('delete_company'),
      }),
    );
  };

  updateCompanyContent = (id: CompanyId, company: Partial<CompanyResponse>) => {
    return CompanyAPI.updateCompanyContent(id, company).pipe(
      this.companiesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_company'),
      }),
    );
  };

  removeLogo = (companyId: CompanyId, type: string) => {
    const typeLabel = type == 'favicon' ? 'Favicon' : 'Logo';
    return showDeleteConfirmation(`Delete ${typeLabel}`, `Do you really want to remove this ${R.toLower(typeLabel)}?`).pipe(
      switchMap(() => CompanyAPI.removeLogo(companyId, type)),
      this.companiesRepo.ops.upsertOne(item => ({ item })),
      tap(() => Session.reloadUser().subscribe()),
      handleMessage({
        type: requestMessage('update_company'),
      }),
    );
  };
}
